/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useEffect, useState } from 'react';

import { jsx } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { IntlMessagesProvider } from '@atlaskit/intl-messages-provider';
import { Box } from '@atlaskit/primitives/compiled';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { token } from '@atlaskit/tokens';
import type {
	ActivityTab,
	AuditLogsSidePanelProps,
	QueryTab,
	SidePanelActions,
	SidePanelControllerState,
} from '@atlassian/audit-logs-common';
import Sidebar from '@atlassian/sidebar';

import { SidePanelController, useSidePanel } from '../controllers';

import { RelatedActivitiesTab } from './header/related-activities-tab';
import { messages } from './messages';
import { DetailsPanel } from './tab-panels/details-panel';
import { JSONTabPanel } from './tab-panels/json-panel';
import { QueryDetailsPanel } from './tab-panels/query-details-panel';
import { RelatedActivitiesTabPanel } from './tab-panels/related-activities-panel';

const styles = cssMap({
	noDoubleScroll: {
		position: 'relative',
	},
	tabPanelContent: {
		paddingTop: token('space.200'),
	},
});

// ----- Internal View Component (uses controller context) -----
const SidePanelView = () => {
	const { state: panel, dispatch } = useSidePanel();
	const { formatMessage } = useIntl();

	if (!panel) {
		return null;
	}

	const handleClose = () => dispatch({ type: 'CLOSE' });

	// Activity Details Mode
	if (panel.mode === 'activity-details') {
		const shouldShowRelatedActivitiesTab = !!panel.relatedEvents.correlationId;
		const activityTabs: ActivityTab[] = shouldShowRelatedActivitiesTab
			? ['details', 'related-activities', 'json']
			: ['details', 'json'];

		const tabIndex = activityTabs.indexOf(panel.activeTab);

		const handleActivityTabChange = (index: number) => {
			const tab = activityTabs[index];
			dispatch({ type: 'SET_TAB', tab });
		};

		return (
			<Sidebar
				title={formatMessage(messages.inspectViewTitle)}
				onClose={handleClose}
				mainContent={
					<Box xcss={styles.noDoubleScroll}>
						<Tabs id="activity-details-tabs" selected={tabIndex} onChange={handleActivityTabChange}>
							<TabList>
								<Tab>
									<FormattedMessage {...messages.detailsTab} />
								</Tab>
								{shouldShowRelatedActivitiesTab && (
									<Tab>
										<RelatedActivitiesTab
											isLoading={panel.relatedEvents.isLoading}
											hasError={panel.relatedEvents.hasError}
											hasMoreEvents={panel.relatedEvents.hasMoreEvents}
											totalEventsCount={panel.relatedEvents.totalEventsCount}
											isSelected={panel.activeTab === 'related-activities'}
										/>
									</Tab>
								)}
								<Tab>
									<FormattedMessage {...messages.jsonTab} />
								</Tab>
							</TabList>
							<TabPanel>
								<Box xcss={styles.tabPanelContent}>
									<DetailsPanel event={panel.activityData} />
								</Box>
							</TabPanel>
							{shouldShowRelatedActivitiesTab && (
								<TabPanel>
									<Box xcss={styles.tabPanelContent}>
										<RelatedActivitiesTabPanel relatedEvents={panel.relatedEvents} />
									</Box>
								</TabPanel>
							)}
							<TabPanel>
								<Box xcss={styles.tabPanelContent}>
									<JSONTabPanel
										event={panel.activityData}
										eventJson={JSON.stringify(panel.activityData, null, 2)}
									/>
								</Box>
							</TabPanel>
						</Tabs>
					</Box>
				}
			/>
		);
	}

	// Query Details Mode (panel.mode === 'query-details')
	const queryTabs: QueryTab[] = ['query-details', 'example-second-tab'];
	const queryTabIndex = queryTabs.indexOf(panel.activeTab);

	const handleQueryTabChange = (index: number) => {
		const tab = queryTabs[index];
		dispatch({ type: 'SET_TAB', tab });
	};

	return (
		<Sidebar
			title={formatMessage(messages.inspectViewTitle)}
			onClose={handleClose}
			mainContent={
				<Box xcss={styles.noDoubleScroll}>
					<Tabs id="query-details-tabs" selected={queryTabIndex} onChange={handleQueryTabChange}>
						<TabList>
							<Tab>
								<FormattedMessage {...messages.queryDetailsTab} />
							</Tab>
							<Tab>
								<FormattedMessage {...messages.jsonTab} />
							</Tab>
						</TabList>
						<TabPanel>
							<Box xcss={styles.tabPanelContent}>
								<QueryDetailsPanel queryData={panel.queryData} />
							</Box>
						</TabPanel>
						<TabPanel>
							<Box xcss={styles.tabPanelContent}>
								<JSONTabPanel eventJson={JSON.stringify(panel.queryData)} />
							</Box>
						</TabPanel>
					</Tabs>
				</Box>
			}
		/>
	);
};

// ----- Main Component (manages state and provides controller) -----
const AuditLogsSidePanelComponent = ({
	sidePanel,
	onClose,
	onTabChange,
}: AuditLogsSidePanelProps) => {
	const { activeMode, activity, query } = sidePanel;

	// Derive the mode for internal state
	const mode = activeMode === 'activity' ? 'activity-details' : 'query-details';

	// Default tab values when configs are not provided
	const activityActiveTab = activity?.activeTab ?? 'details';
	const queryActiveTab = query?.activeTab ?? 'query-details';

	// Build panel state from structured props
	const [panelState, setPanelState] = useState<SidePanelControllerState>(() => {
		if (mode === 'query-details' && query) {
			return {
				mode: 'query-details',
				queryData: query.queryData,
				activeTab: queryActiveTab,
				tabHistory: {
					'activity-details': activityActiveTab,
					'query-details': queryActiveTab,
				},
			};
		}
		if (mode === 'activity-details' && activity) {
			return {
				mode: 'activity-details',
				activityData: activity.activityData,
				relatedEvents: activity.relatedEvents,
				activeTab: activityActiveTab,
				tabHistory: {
					'activity-details': activityActiveTab,
					'query-details': queryActiveTab,
				},
			};
		}
		// No matching config for the active mode
		return null;
	});

	// Sync panel state when props change (mode switch, tab change, data change)
	useEffect(() => {
		if (mode === 'query-details' && query) {
			setPanelState({
				mode: 'query-details',
				queryData: query.queryData,
				activeTab: queryActiveTab,
				tabHistory: {
					'activity-details': activityActiveTab,
					'query-details': queryActiveTab,
				},
			});
		} else if (mode === 'activity-details' && activity) {
			setPanelState({
				mode: 'activity-details',
				activityData: activity.activityData,
				relatedEvents: activity.relatedEvents,
				activeTab: activityActiveTab,
				tabHistory: {
					'activity-details': activityActiveTab,
					'query-details': queryActiveTab,
				},
			});
		} else {
			setPanelState(null);
		}
	}, [mode, activity, activityActiveTab, query, queryActiveTab]);

	// External actions (owned by this component)
	const actions: SidePanelActions = {
		setActivityTab: (tab) => {
			setPanelState((prev) =>
				prev && prev.mode === 'activity-details' ? { ...prev, activeTab: tab } : prev,
			);
			if (onTabChange) {
				onTabChange(tab);
			}
		},
		setQueryTab: (tab) => {
			setPanelState((prev) =>
				prev && prev.mode === 'query-details' ? { ...prev, activeTab: tab } : prev,
			);
			if (onTabChange) {
				onTabChange(tab);
			}
		},
		onClose: () => {
			if (onClose) {
				// Parent controls visibility — just notify
				onClose();
			} else {
				// No external handler — fallback to internal state management
				setPanelState(null);
			}
		},
	};

	return (
		<SidePanelController state={panelState} actions={actions}>
			<SidePanelView />
		</SidePanelController>
	);
};

// ----- Public Export (with IntlMessagesProvider wrapper) -----
export const AuditLogsSidePanel = (props: AuditLogsSidePanelProps) => (
	<IntlMessagesProvider loaderFn={() => Promise.resolve({})}>
		<AuditLogsSidePanelComponent {...props} />
	</IntlMessagesProvider>
);
